import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
} from 'react-native';
import Card from '../common/Card';
import Avatar from '../common/Avatar';
import PlatformIcon from '../common/PlatformIcon';

export interface FriendListItem {
  id: string;
  friendUserId: string;
  displayName: string;
  avatarUrl?: string | null;
  bio?: string | null;
  tags: string[];
  tagKeys?: string[];
  raw?: unknown;
}

export interface FriendTagOption {
  value: string;
  label: string;
  count: number;
}

export interface FriendListStrings {
  emptyAllTitle: string;
  emptyAllSubtitle: string;
  emptyTagTitle: string;
  emptyTagSubtitle: string;
  addFriendButton: string;
  editTagsA11y: string;
  deleteFriendA11y: string;
}

export interface FriendListProps {
  friends: FriendListItem[];
  tagOptions: FriendTagOption[];
  tagFilter: string;
  selectedFriendId: string | null;
  deleteLoading?: boolean;
  showAddFriendCta?: boolean;
  strings: FriendListStrings;
  onAddFriendPress?: () => void;
  onTagFilterChange?: (value: string) => void;
  onFriendPress?: (friend: FriendListItem) => void;
  onFriendLongPress?: (friend: FriendListItem) => void;
  onEditFriendTags?: (friend: FriendListItem) => void;
  onRemoveFriend?: (friend: FriendListItem) => void;
}

const FriendList: React.FC<FriendListProps> = ({
  friends,
  tagOptions,
  tagFilter,
  selectedFriendId,
  deleteLoading = false,
  showAddFriendCta = false,
  strings,
  onAddFriendPress,
  onTagFilterChange,
  onFriendPress,
  onFriendLongPress,
  onEditFriendTags,
  onRemoveFriend,
}) => {
  const emptyState = tagFilter === 'all'
    ? { title: strings.emptyAllTitle, subtitle: strings.emptyAllSubtitle }
    : { title: strings.emptyTagTitle, subtitle: strings.emptyTagSubtitle };

  const handleFriendPress = (friend: FriendListItem) => {
    onFriendPress?.(friend);
  };

  const handleFriendLongPress = (friend: FriendListItem) => {
    onFriendLongPress?.(friend);
  };

  return (
    <View>
      {tagOptions.length > 0 && (
        <View style={styles.tagFilter}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {tagOptions.map((option) => {
              const isActive = tagFilter === option.value;
              return (
                <TouchableOpacity
                  key={option.value}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isActive }}
                  style={[styles.filterTag, isActive && styles.filterTagActive]}
                  onPress={() => onTagFilterChange?.(option.value)}
                >
                  <Text style={[styles.filterTagText, isActive && styles.filterTagActiveText]}>
                    {option.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      )}

      {friends.length === 0 ? (
        <Card style={styles.emptyCard}>
          <PlatformIcon name="group" size={48} color="#cccccc" />
          <Text style={styles.emptyText}>{emptyState.title}</Text>
          <Text style={styles.emptySubtext}>{emptyState.subtitle}</Text>
          {showAddFriendCta && (
            <TouchableOpacity
              style={styles.addFriendButton}
              onPress={onAddFriendPress}
              accessibilityRole="button"
            >
              <PlatformIcon name="add" size={20} color="#ffffff" />
              <Text style={styles.addFriendButtonText}>{strings.addFriendButton}</Text>
            </TouchableOpacity>
          )}
        </Card>
      ) : (
        friends.map((friend) => {
          const isSelected = selectedFriendId === friend.id;

          return (
            <Card key={friend.id} style={[styles.itemCard, isSelected && styles.itemCardSelected]}>
              <TouchableOpacity
                style={styles.friendInfo}
                activeOpacity={0.8}
                onPress={() => handleFriendPress(friend)}
                onLongPress={() => handleFriendLongPress(friend)}
              >
                <Avatar
                  size="medium"
                  avatarUrl={friend.avatarUrl || undefined}
                  displayName={friend.displayName}
                />
                <View style={styles.friendDetails}>
                  <Text style={styles.itemTitle}>{friend.displayName}</Text>
                  {!!friend.bio && (
                    <Text style={styles.bioText} numberOfLines={2}>
                      {friend.bio}
                    </Text>
                  )}

                  {friend.tags.length > 0 && (
                    <View style={styles.friendTags}>
                      {friend.tags.slice(0, 3).map((tag, index) => (
                        <View key={`${friend.id}-${tag}-${index}`} style={styles.friendTag}>
                          <Text style={styles.friendTagText}>{tag}</Text>
                        </View>
                      ))}
                      {friend.tags.length > 3 && (
                        <Text style={styles.moreTags}>+{friend.tags.length - 3}</Text>
                      )}
                    </View>
                  )}
                </View>

                {isSelected && (
                  <View style={styles.friendActionButtons}>
                    {onEditFriendTags && (
                      <TouchableOpacity
                        style={styles.friendEditButton}
                        onPress={() => onEditFriendTags(friend)}
                        accessibilityRole="button"
                        accessibilityLabel={strings.editTagsA11y}
                      >
                        <PlatformIcon name="tag" size={16} color="#4A90E2" />
                      </TouchableOpacity>
                    )}

                    {onRemoveFriend && (
                      <TouchableOpacity
                        style={styles.friendDeleteButton}
                        onPress={() => onRemoveFriend(friend)}
                        disabled={deleteLoading}
                        accessibilityRole="button"
                        accessibilityLabel={strings.deleteFriendA11y}
                      >
                        <PlatformIcon name="delete" size={16} color="#FF3B30" />
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </TouchableOpacity>
            </Card>
          );
        })
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  tagFilter: {
    marginBottom: 16,
  },
  filterTag: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#f1f5f9',
    borderRadius: 16,
    marginRight: 8,
  },
  filterTagActive: {
    backgroundColor: '#dbeafe',
  },
  filterTagText: {
    fontSize: 13,
    color: '#475569',
  },
  filterTagActiveText: {
    color: '#1d4ed8',
    fontWeight: '600',
  },
  emptyCard: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 16,
  },
  emptyText: {
    fontSize: 18,
    color: '#475569',
    marginBottom: 8,
    marginTop: 16,
    fontWeight: '600',
  },
  emptySubtext: {
    fontSize: 14,
    color: '#94a3b8',
    marginBottom: 20,
    textAlign: 'center',
  },
  addFriendButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2563eb',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
  },
  addFriendButtonText: {
    color: '#ffffff',
    fontSize: 14,
    marginLeft: 8,
    fontWeight: '500',
  },
  itemCard: {
    marginBottom: 10,
    padding: 12,
  },
  itemCardSelected: {
    backgroundColor: '#F0F8FF',
    borderColor: '#2563eb',
    borderWidth: 1,
  },
  friendInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  friendDetails: {
    flex: 1,
    marginLeft: 12,
  },
  itemTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 2,
  },
  bioText: {
    fontSize: 13,
    color: '#64748b',
    marginTop: 2,
  },
  friendTags: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    flexWrap: 'wrap',
  },
  friendTag: {
    backgroundColor: '#e2e8f0',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginRight: 6,
    marginBottom: 4,
  },
  friendTagText: {
    fontSize: 12,
    color: '#475569',
  },
  moreTags: {
    fontSize: 12,
    color: '#64748b',
    marginLeft: 4,
  },
  friendActionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 12,
  },
  friendEditButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#E3F2FD',
    justifyContent: 'center',
    alignItems: 'center',
    // Use CSS boxShadow on web to avoid react-native-web deprecation warnings
    ...Platform.select ? Platform.select({
      web: { boxShadow: '0 1px 2px rgba(74,144,226,0.12)' },
      default: { shadowColor: '#4A90E2', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 2, elevation: 1 },
    }) : { shadowColor: '#4A90E2', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 2, elevation: 1 },
  },
  friendDeleteButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#fee2e2',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
});

export default FriendList;
